import { Drawer, Typography, Box } from "@mui/material";
import ButtonElement from "../../UI/ButtonElement/ButtonElement";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ButtonsData,
  DownloadBillListReqBody,
  FiltersBills,
  SelectedRows,
} from "../../../core/models/bills/bills.model";
import TotalEnergiesContext from "../../../core/store/context/TotalEnergiesContext";
import { getBillsIdDocumentsListRequestBody } from "../../../utils/components/bills/bills.util";
import {
  downloadBillsList,
  getBillsIdDocumentsList,
} from "../../../core/services/bills/bills.service";
import { v4 as uuidv4 } from "uuid";
import "./BillsDownload.scss";
import { GroupedSuppliesTableRow } from "../../../core/models/supplies/supplies.model";

export interface BillsDownloadProps {
  isDownloadDrawerOpen: boolean;
  isSelectedDownloader: boolean;
  setIsDownloadDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setIsSelectedDownloader: React.Dispatch<React.SetStateAction<boolean>>;
  selectedRows: SelectedRows[];
  filtersBills: FiltersBills;
  selectedSupply: GroupedSuppliesTableRow | null;
  isFilterApplied: boolean;
}

const BillsDownload = ({
  isDownloadDrawerOpen,
  isSelectedDownloader,
  setIsDownloadDrawerOpen,
  setIsSelectedDownloader,
  selectedRows,
  filtersBills,
  selectedSupply,
  isFilterApplied,
}: BillsDownloadProps) => {
  const { t } = useTranslation();
  const { ownerData, userData, setSnackbarData } =
    useContext(TotalEnergiesContext);
  const holderData = JSON.parse(localStorage.getItem("holderData") ?? "");
  const userEmail = (holderData.emailLogin as string) ?? "";

  const [billsList, setBillsList] = useState<number[]>([]);
  const [fileType, setFileType] = useState<string>("");
  const [pendingFileTypes, setPendingFileTypes] = useState<string[]>([]);
  const [disabledFileTypes, setDisabledFileTypes] = useState<string[]>([]);
  const pendingFileTypesRef = useRef(new Set<string>());
  const [requestId, setRequestId] = useState<string>("");
  const getBillsIdDocumentsListAbort = new AbortController();

  const handleClose = useCallback(() => {
    setIsDownloadDrawerOpen(false);
    setIsSelectedDownloader(false);
    setBillsList([]);
    setDisabledFileTypes([]);
  }, [setIsDownloadDrawerOpen, setIsSelectedDownloader]);

  const getDownloadBillsList = useCallback(
    async (
      billsIdList: number[],
      fileType: string,
      requestId: string,
      abortSignal: AbortSignal,
    ) => {
      const requestBody: DownloadBillListReqBody = {
        to: userEmail,
        type: "00",
        fileType,
        requestId,
        billIdList: billsIdList,
      };

      try {
        await downloadBillsList(
          requestBody,
          userData?.token || "",
          abortSignal,
        );

        handleClose();
        setSnackbarData({
          isOpen: true,
          isSuccessSeverity: true,
          text: "Facturas Página.Se está procesando tu descarga",
        });
      } catch (error) {
        console.error("Error downloading bill list", error);
        handleClose();
        setSnackbarData({
          isOpen: true,
          isSuccessSeverity: false,
          text: "Facturas Página.El servicio no está disponible ahora",
        });
      } finally {
        pendingFileTypesRef.current.delete(fileType);
        setPendingFileTypes((currentFileTypes) =>
          currentFileTypes.filter(
            (pendingFileType) => pendingFileType !== fileType,
          ),
        );
      }
    },
    [handleClose, setSnackbarData, userData?.token],
  );

  useEffect(() => {
    const getBillsDownloadListAbort = new AbortController();

    if (billsList.length) {
      getDownloadBillsList(
        billsList,
        fileType,
        requestId,
        getBillsDownloadListAbort.signal,
      );
    }
  }, [billsList, fileType, getDownloadBillsList, requestId]);

  const buttonsData: ButtonsData[] = [
    {
      id: "pdf",
      label: "Descargar PDF",
      value: "pdf",
    },
    {
      id: "xls",
      label: "Descargar Excel",
      value: "xls",
    },
  ];

  const handleClick = (fileType: string) => {
    if (
      pendingFileTypesRef.current.has(fileType) ||
      disabledFileTypes.includes(fileType)
    ) {
      return;
    }

    pendingFileTypesRef.current.add(fileType);
    setPendingFileTypes((currentFileTypes) => [...currentFileTypes, fileType]);
    handleClickDownloadReset();
    setFileType(fileType);

    const newRequestId = uuidv4();
    setRequestId(newRequestId);

    if (isSelectedDownloader) {
      const billsIdList = selectedRows.map((selectedRow) =>
        Number(selectedRow[fileType === "pdf" ? "id" : "idExcel"]),
      );

      if (billsIdList.length) {
        setBillsList(billsIdList);
      } else {
        pendingFileTypesRef.current.delete(fileType);
        setPendingFileTypes((currentFileTypes) =>
          currentFileTypes.filter(
            (pendingFileType) => pendingFileType !== fileType,
          ),
        );
      }
      return;
    }
    handleClickDownloadAll(fileType);
  };

  const handleClickDownloadAll = (fileType: string) => {
    const requestBody = getBillsIdDocumentsListRequestBody(
      ownerData,
      filtersBills,
      selectedSupply,
    );

    getBillsIdDocumentsList(
      requestBody,
      userData?.token || "",
      getBillsIdDocumentsListAbort.signal,
    )
      .then(
        async ({
          Gas: {
            IdsDocumento: documentsGasIds,
            IdsFacturaVentaCabecera: excelDocumentsGasIds,
          },
          Luz: {
            IdsDocumento: documentsLightsIds,
            IdsFacturaVentaCabecera: excelDocumentsLightIds,
          },
        }) => {
          const billsIdList =
            fileType === "pdf"
              ? [...documentsGasIds, ...documentsLightsIds].map(Number)
              : [...excelDocumentsGasIds, ...excelDocumentsLightIds].map(
                  Number,
                );

          setDisabledFileTypes((currentFileTypes) =>
            currentFileTypes.includes(fileType)
              ? currentFileTypes
              : [...currentFileTypes, fileType],
          );
          setBillsList(billsIdList);
        },
      )
      .catch(() => {
        handleClose();
        setSnackbarData({
          isOpen: true,
          isSuccessSeverity: false,
          text: "Facturas Página.El servicio no está disponible ahora",
        });
      })
      .finally(() => {
        pendingFileTypesRef.current.delete(fileType);
        setPendingFileTypes((currentFileTypes) =>
          currentFileTypes.filter(
            (pendingFileType) => pendingFileType !== fileType,
          ),
        );
      });
  };

  const handleClickDownloadReset = () => {
    setBillsList([]);
  };

  return (
    <Drawer
      className={`bills-download__drawer`}
      open={isDownloadDrawerOpen}
      anchor="bottom"
      PaperProps={{
        sx: { padding: "3rem  5.5rem" },
        className: "bills-download",
      }}
    >
      <Typography component="h3" variant="h3">
        {t("Facturas Página.Elige un formato de descarga")}
      </Typography>
      <Typography component="p" variant="body2">
        {t(
          "Facturas Página.Debes tener en cuenta que la descarga de un volumen alto de facturas puede conllevar mucho tiempo.",
        )}
      </Typography>
      {!!isSelectedDownloader && (
        <Typography component="p" variant="subtitle1" fontWeight={400}>
          {selectedRows.length}{" "}
          {t(
            `Facturas Página.${
              selectedRows.length === 1
                ? "Factura seleccionada"
                : "Facturas seleccionadas"
            }`,
          )}
        </Typography>
      )}
      <Box
        component="div"
        display="flex"
        justifyContent="space-between"
        className="bills-download__buttons"
      >
        <Box component="div" display="flex" gap="1.5rem">
          {buttonsData.map(({ id, label, value }) => (
            <ButtonElement
              key={id}
              secondary
              iconL
              disabled={
                pendingFileTypes.includes(value) ||
                disabledFileTypes.includes(value)
              }
              onClick={() => handleClick(value)}
              iconName="t-icon-pdf"
            >
              {t(`Facturas Página.${label}`)}
            </ButtonElement>
          ))}
        </Box>
        <ButtonElement onClick={handleClose} link>
          {t("Facturas Página.Cancelar")}
        </ButtonElement>
      </Box>
    </Drawer>
  );
};

export default BillsDownload;
